import React, { useState, useEffect, useCallback } from 'react';
import { testimonialsAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Check, Star, Clock } from 'lucide-react';

const AdminTestimonials = () => {
  const [approved, setApproved] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [approvedData, pendingData] = await Promise.all([
        testimonialsAPI.getTestimonials(),
        testimonialsAPI.getPendingTestimonials(),
      ]);
      setApproved(approvedData || []);
      setPending(pendingData || []);
    } catch (err) { setError('Failed to load testimonials'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleApprove = async (id) => {
    try {
      await testimonialsAPI.approveTestimonial(id);
      fetchAll();
    } catch (err) { setError('Failed to approve testimonial'); }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`w-3 h-3 ${i < (rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} />
    ));
  };

  return (
    <div data-testid="admin-testimonials">
      <h1 className="text-2xl font-bold text-white mb-6">Testimonials</h1>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="bg-slate-900 border border-slate-800 mb-4">
          <TabsTrigger value="pending" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white text-slate-400">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white text-slate-400">
            Approved ({approved.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {loading ? (
            <div className="text-slate-400">Loading...</div>
          ) : pending.length === 0 ? (
            <div className="text-center text-slate-400 py-12">No pending testimonials.</div>
          ) : (
            <div className="space-y-3">
              {pending.map(t => (
                <Card key={t.id} className="bg-slate-900 border-slate-800 border-l-4 border-l-amber-500" data-testid={`pending-testimonial-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-white font-medium">{t.name}</h3>
                          <Badge className="bg-amber-500/10 text-amber-400 text-xs"><Clock className="w-3 h-3 mr-1" />Pending</Badge>
                          <div className="flex">{renderStars(t.rating)}</div>
                        </div>
                        <p className="text-slate-400 text-xs mb-2">{t.treatment}</p>
                        <p className="text-slate-300 text-sm">{t.text}</p>
                      </div>
                      <Button size="sm" onClick={() => handleApprove(t.id)} className="bg-green-600 hover:bg-green-500 text-white ml-3" data-testid={`approve-testimonial-${t.id}`}>
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved">
          {loading ? (
            <div className="text-slate-400">Loading...</div>
          ) : approved.length === 0 ? (
            <div className="text-center text-slate-400 py-12">No approved testimonials.</div>
          ) : (
            <div className="space-y-3">
              {approved.map(t => (
                <Card key={t.id} className="bg-slate-900 border-slate-800" data-testid={`approved-testimonial-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-white font-medium">{t.name}</h3>
                      <Badge className="bg-green-500/10 text-green-400 text-xs"><Check className="w-3 h-3 mr-1" />Approved</Badge>
                      <div className="flex">{renderStars(t.rating)}</div>
                    </div>
                    <p className="text-slate-400 text-xs mb-2">{t.treatment}</p>
                    <p className="text-slate-300 text-sm">{t.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminTestimonials;
